import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert, Tooltip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { downloadHansardPdf } from '../services/api';

/**
 * DownloadPdfButton — downloads a single Hansard fragment as a PDF.
 * Mirrors the ExportCSVButton pattern (loading state, success/error
 * Snackbars, accessible tooltip).
 *
 * @param {string} contentId - the Hansard fragment id
 * @param {string} size      - MUI button size (default 'small')
 */
export default function DownloadPdfButton({ contentId, size = 'small' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleDownload = async () => {
    if (loading || !contentId) return;
    setLoading(true);
    setError(null);
    try {
      await downloadHansardPdf(contentId);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'PDF download failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tooltipText = 'Download this Hansard fragment as a PDF';

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <span>
          <Button
            variant="outlined"
            size={size}
            startIcon={loading
              ? <CircularProgress size={16} aria-label="Generating PDF" />
              : <PictureAsPdfIcon />}
            onClick={handleDownload}
            disabled={loading || !contentId}
            aria-busy={loading}
            aria-label={loading ? 'Generating PDF, please wait' : tooltipText}
            sx={{
              textTransform: 'none',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            {loading ? 'Preparing…' : 'Download PDF'}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" role="alert" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" role="status" onClose={() => setSuccess(false)}>
          PDF download started. Check your downloads folder.
        </Alert>
      </Snackbar>
    </>
  );
}
